// ============================================================================
// Management-fee parse/format/persist helpers for the New Account SOP intake.
//
// The management fee lives on facilities.management_fee_pct (numeric(6,4),
// stored as a FRACTION: 0.0300 = 3%) and is carried from the SOP draft to the
// facility on materialization via nas_create_facility_for_card().
//
// Business rule (Ben, confirmed 2026-07-02 / re-confirmed 2026-07-14):
//   * Default ON at intake, prefilled to 3%, but fully EDITABLE and REMOVABLE.
//   * "Management fee?" toggle OFF  => persist explicit 0 (NOT null). 0 is
//     arithmetic-safe for downstream revenue×fee math and keeps the column
//     null-free.
//   * There is NO DB default and NO overwriting trigger (the 7/2 silent-3%
//     default + backfill incident). Fees are set by humans and must stick.
//
// This is the management fee ONLY — never annual_kicker_pct (the inflationary
// kicker), which is a separate column with separate math.
// ============================================================================

/** The default management fee, as a percent string for the input field. */
export const DEFAULT_MGMT_FEE_PCT = '3';

/** The default management fee, as the stored fraction (3% = 0.03). */
export const DEFAULT_MGMT_FEE_FRACTION = 0.03;

/**
 * Percent string as typed by the user ("3", "2.5") → stored fraction (0.03,
 * 0.025). Blank / whitespace / non-finite / negative all return null so the
 * caller can treat "fee on but nothing entered" as invalid (see canSubmit).
 */
export function feePctToFraction(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  // Round to 4 dp to match numeric(6,4). 3 → 0.0300, 2.5 → 0.0250.
  return Math.round(n * 100) / 10000;
}

/**
 * Stored fraction (0.03) → editable percent string ("3"). null / 0 render as
 * "" so the field is empty when there is no fee. Positive fractions render as
 * their percent (0.025 → "2.5").
 */
export function fractionToFeePct(v: number | null | undefined): string {
  if (v == null || v === 0) return '';
  return String(Math.round(v * 10000) / 100);
}

/**
 * Resolve the value to persist to facilities.management_fee_pct given the
 * toggle state and the entered percent.
 *   - toggle OFF          => 0            (explicit "no fee")
 *   - toggle ON + valid %  => that fraction
 *   - toggle ON + blank/invalid => `fallback` (default 3%), so an untouched
 *     intake with the default toggle lands at 0.0300.
 * Always returns a number — never null — so the column stays null-free.
 */
export function mgmtFeeToPersist(
  feeOn: boolean,
  pctStr: string,
  fallback: number = DEFAULT_MGMT_FEE_FRACTION,
): number {
  if (!feeOn) return 0;
  const frac = feePctToFraction(pctStr);
  return frac == null ? fallback : frac;
}

/**
 * Derive the intake toggle + percent-string from a stored fraction when
 * resuming a draft or reflecting an existing value.
 *   - fraction === 0        => fee OFF, "" (explicit no fee)
 *   - fraction > 0          => fee ON, that percent
 *   - null/undefined (unset) => fee ON + default 3% prefill (new-record default)
 */
export function feeStateFromFraction(v: number | null | undefined): {
  feeOn: boolean;
  pct: string;
} {
  if (v === 0) return { feeOn: false, pct: '' };
  if (v == null) return { feeOn: true, pct: DEFAULT_MGMT_FEE_PCT };
  return { feeOn: true, pct: fractionToFeePct(v) };
}
